import React, {
    Fragment,
    useContext,
    useEffect,
    useMemo,
    useReducer,
} from "react";
import Toolbar, { ToolbarGroup } from "components/Toolbar/Toolbar";
import ToolbarButton from "components/Toolbar/ToolbarButton";
import {
    SaveIcon,
    LockClosedIcon,
    LockOpenIcon,
    BanIcon,
    TrashIcon,
    SupportIcon,
} from "@heroicons/react/outline";
import { AssignmentContext } from "context/Assignment";
import { useAuth } from "hooks/Auth";
import { apiPost, apiDel, messageFromException } from "helpers/Request";
import { toast } from "react-hot-toast";
import { useFormikContext } from "formik";
import { useHistory } from "react-router-dom";
import { useContextualHelp } from "hooks/Help";
import { useRegisterActions } from "kbar";
import { modal } from "components/HeadlessModal";
import DangerButton from "components/Buttons/DangerButton";
import SecondaryButton from "components/Buttons/SecondaryButton";

const reducer = (oldState, newState) => {
    return Object.assign({}, oldState, newState);
};

export default function AssignmentToolbar(props) {
    const {
        assignment,
        requestReload,
        preformingStateUpdate,
        setPreformingStateUpdate,
        saving,
    } = useContext(AssignmentContext);
    const auth = useAuth();
    const history = useHistory();

    const [state, setState] = useReducer(reducer, {
        isLocked: false,
        isUserEditing: false,
        blockingButton: false,
    });

    const { submitForm } = useFormikContext();
    const contextualHelp = useContextualHelp();

    const allKbarActions = [
        {
            id: "assignmentCheckout",
            name: "Check Out",
            keywords: "checkout",
            perform: () => {
                checkOut();
            },
        },
        {
            id: "assignmentCheckin",
            name: "Check In",
            keywords: "checkin",
            perform: () => {
                checkIn();
            },
        },
        {
            id: "assignmentSaveCheckin",
            name: "Save and Check In",
            keywords: "save checkin",
            perform: () => {
                saveCheckIn();
            },
        },
    ];

    const lockState = useMemo(() => {
        if (!assignment || !assignment.uuid) {
            return "new";
        }
        if (assignment.lock && assignment.lock.user.uuid === auth.user.uuid) {
            return "locked-self";
        } else if (
            assignment.lock &&
            assignment.lock.user.uuid !== auth.user.uuid
        ) {
            return "locked-other";
        } else {
            return "available";
        }
    }, [assignment, auth.user]);

    const kbarActions = useMemo(() => {
        return allKbarActions.filter((action) => {
            if (
                action.id === "assignmentCheckout" &&
                lockState === "available"
            ) {
                return true;
            } else if (
                (action.id === "assignmentCheckin" ||
                    action.id === "assignmentSaveCheckin") &&
                lockState === "locked-self"
            ) {
                return true;
            }

            return false;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignment, lockState]);

    useRegisterActions(kbarActions, [kbarActions]);

    const checkOut = () => {
        if (!assignment.uuid) {
            console.log("no uuid");
            return;
        }
        if (assignment.lock) {
            console.log("locked");
            return;
        }

        setState({ blockingButton: "check-out" });
        setPreformingStateUpdate(true);
        apiPost("/v4/lock-request", auth.publication, { srn: assignment.srn })
            .then((resp) => {
                requestReload();
                setPreformingStateUpdate(false);
                setState({ blockingButton: false });
            })
            .catch((err) => {
                toast.error("Unable to acquire lock");
            });
    };

    const clearLock = () => {
        if (!assignment.uuid) {
            return;
        }
        if (!assignment.lock || !auth.user.can("admin")) {
            return;
        }
        modal.warning({
            title: "Clear Lock",
            children: (
                <Fragment>
                    Forcing a lock to clear will make this item immediately
                    available, but discard any pending changes. Are you sure you
                    want to continue?
                </Fragment>
            ),
            buttons: () => (
                <Fragment>
                    <DangerButton onClick={onClearLock}>
                        Force Unlock
                    </DangerButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const onClearLock = () => {
        setState({ blockingButton: "clear-lock" });
        setPreformingStateUpdate(true);

        const id = toast.loading("Clearing lock...");
        modal.update({
            disabled: true,
        });

        apiDel(
            `/v4/lock-request/${assignment.lock.uuid}?force_override=${auth.user.uuid}`,
            auth.publication
        )
            .then((resp) => {
                requestReload();
                setPreformingStateUpdate(false);
                setState({ blockingButton: false });
                modal.dismiss();
                toast.success("Lock cleared", { id });
            })
            .catch((err) => {
                modal.dismiss();
                toast.dismiss(id);
                modal.error({
                    title: "Unable to clear lock",
                    children: messageFromException(err),
                });
            });
    };

    const checkIn = () => {
        if (!assignment.uuid) {
            console.log("no uuid");
            return;
        }
        if (!assignment.lock || assignment.lock.user.uuid !== auth.user.uuid) {
            console.log("no lock");
            return;
        }
        setState({ blockingButton: "check-in" });
        setPreformingStateUpdate(true);

        apiDel("/v4/lock-request/" + assignment.lock.uuid, auth.publication)
            .then((resp) => {
                requestReload();
                setPreformingStateUpdate(false);
                setState({ blockingButton: false });
            })
            .catch((err) => {
                toast.error("Unable to release lock");
            });
    };

    const saveCheckIn = () => {
        if (!assignment.uuid) {
            return;
        }
        if (!assignment.lock || assignment.lock.user.uuid !== auth.user.uuid) {
            return;
        }

        setState({ blockingButton: "save-check-in" });
        setPreformingStateUpdate(true);
        submitForm().then(() => {
            apiDel("/v4/lock-request/" + assignment.lock.uuid, auth.publication)
                .then((resp) => {
                    requestReload();
                    setPreformingStateUpdate(false);
                    setState({ blockingButton: false });
                })
                .catch((err) => {
                    toast.error("Unable to release lock");
                });
        });
    };

    const del = () => {
        if (!assignment.uuid) {
            return;
        }
        if (!assignment.lock || assignment.lock.user.uuid !== auth.user.uuid) {
            return;
        }

        const uuid = assignment.uuid;

        modal.warning({
            title: "Delete Items",
            confirmText: "Delete",
            children: (
                <Fragment>
                    Are you sure you want to delete this and all saved versions?
                    This cannot be undone.
                </Fragment>
            ),
            buttons: () => (
                <Fragment>
                    <DangerButton onClick={() => onDelete(uuid)}>
                        Delete
                    </DangerButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const onDelete = (uuid) => {
        const id = toast.loading("Deleting...");

        setState({ blockingButton: "delete" });
        setPreformingStateUpdate(true);
        modal.update({
            disabled: true,
        });

        apiDel(`/v4/assignment/${uuid}`, auth.publication)
            .then(() => {
                toast.success("Assignment deleted", { id });
                modal.dismiss();
                history.push("/app/planner");
            })
            .catch((err) => {
                setState({ blockingButton: false });
                setPreformingStateUpdate(false);
                modal.dismiss();
                toast.dismiss(id);
                modal.error({
                    title: "Unable to delete assignment",
                    children: messageFromException(err),
                });
            });
    };

    useEffect(() => {
        if (!assignment || !assignment.uuid) {
            setState({ isLocked: false, isUserEditing: true });
            return;
        }
        if (assignment.lock && assignment.lock.user.uuid === auth.user.uuid) {
            setState({ isLocked: true, isUserEditing: true });
        } else if (
            assignment.lock &&
            assignment.lock.user.uuid !== auth.user.uuid
        ) {
            setState({ isLocked: true, isUserEditing: false });
        } else {
            setState({ isLocked: false, isUserEditing: false });
        }
    }, [assignment, auth.user]);

    return (
        <Toolbar className="overflow-y-auto">
            <ToolbarGroup>
                <ToolbarButton
                    icon={<SaveIcon />}
                    type="submit"
                    disabled={
                        state.isUserEditing && !preformingStateUpdate
                            ? false
                            : true
                    }
                    blocking={saving && !state.blockingButton}
                >
                    Save
                </ToolbarButton>
                <ToolbarButton
                    icon={<LockClosedIcon />}
                    blocking={state.blockingButton === "save-check-in"}
                    onClick={saveCheckIn}
                    disabled={
                        state.isUserEditing &&
                        assignment.uuid &&
                        !preformingStateUpdate
                            ? false
                            : true
                    }
                    className="hidden xl:flex"
                >
                    Save & Check-in
                </ToolbarButton>
            </ToolbarGroup>
            <ToolbarGroup>
                <ToolbarButton
                    icon={<LockClosedIcon />}
                    disabled={
                        state.isUserEditing &&
                        assignment.uuid &&
                        !preformingStateUpdate
                            ? false
                            : true
                    }
                    onClick={checkIn}
                    blocking={state.blockingButton === "check-in"}
                >
                    Check-in
                </ToolbarButton>
                <ToolbarButton
                    icon={<LockOpenIcon />}
                    disabled={
                        !state.isUserEditing &&
                        !state.isLocked &&
                        !preformingStateUpdate
                            ? false
                            : true
                    }
                    onClick={checkOut}
                    blocking={state.blockingButton === "check-out"}
                >
                    Check-out
                </ToolbarButton>
            </ToolbarGroup>
            <ToolbarGroup>
                <ToolbarButton
                    icon={<BanIcon />}
                    disabled={
                        state.isLocked &&
                        !preformingStateUpdate &&
                        auth.user.can("admin")
                            ? false
                            : true
                    }
                    blocking={state.blockingButton === "unlock"}
                    onClick={clearLock}
                >
                    Unlock
                </ToolbarButton>
                <ToolbarButton
                    icon={<TrashIcon />}
                    onClick={del}
                    disabled={
                        state.isLocked &&
                        !preformingStateUpdate &&
                        auth.user.can("assignment", "delete")
                            ? false
                            : true
                    }
                    blocking={state.blockingButton === "delete"}
                >
                    Delete
                </ToolbarButton>
            </ToolbarGroup>
            <ToolbarGroup className="ml-auto">
                <ToolbarButton
                    icon={<SupportIcon />}
                    onClick={() => {
                        contextualHelp.showContextualHelp(
                            `/planner/assignments.html`
                        );
                    }}
                    className="text-green-600"
                >
                    Help
                </ToolbarButton>
            </ToolbarGroup>
        </Toolbar>
    );
}
